// SPDX-License-Identifier: GPL-3.0-or-later
// The synthwave backdrop: one full-screen quad with one fragment shader. It draws a dark sky, a
// low sun and a neon grid that runs toward the viewer, in token colours only. It sits behind the
// table (it draws first and never writes depth). It is hidden when the stream shows through the
// canvas (`setVisible(false)`). The picture is always still: nothing in the shader changes with
// time, so the backdrop never pulls the eye during play. The shader holds no text.

import { Mesh, PlaneGeometry, ShaderMaterial } from "three";
import { colorOf, TOKEN_HEX } from "./vfx/palette.ts";

/** The picture is still at this time (seconds): the grid shows with a good phase. */
export const STATIC_TIME_S = 0.25;

const VERTEX = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 1.0, 1.0);
}
`;

const FRAGMENT = `
varying vec2 vUv;
uniform float uAspect;
uniform vec3 uGrid;
uniform vec3 uHot;
uniform vec3 uSun;
const float HORIZON = 0.46;

vec3 sky(vec2 p) {
  float t = (p.y - HORIZON) / (1.0 - HORIZON);
  vec3 col = uHot * 0.3 * (1.0 - smoothstep(0.0, 0.65, t));
  vec2 q = (p - vec2(0.5, HORIZON + 0.17)) * vec2(uAspect, 1.0);
  float disc = 1.0 - smoothstep(0.155, 0.165, length(q));
  float band = step(0.35, fract(p.y * 26.0 - ${STATIC_TIME_S.toFixed(3)} * 0.312));
  float cut = mix(1.0, band, 1.0 - smoothstep(HORIZON + 0.04, HORIZON + 0.17, p.y));
  return mix(col, mix(uHot, uSun, smoothstep(HORIZON, HORIZON + 0.34, p.y)), disc * cut);
}

vec3 ground(vec2 p) {
  float depth = HORIZON - p.y;
  float z = 0.07 / max(depth, 0.001);
  float gx = abs(fract((p.x - 0.5) * uAspect * z * 0.45 + 0.5) - 0.5);
  float gz = abs(fract(z - ${STATIC_TIME_S.toFixed(3)} * 0.4) - 0.5);
  float width = 0.04 + 0.012 * min(z, 8.0);
  float line = 1.0 - smoothstep(0.0, width, min(gx, gz));
  float fade = smoothstep(0.0, 0.09, depth);
  vec3 floorGlow = uHot * 0.12 * (1.0 - smoothstep(0.0, 0.2, depth));
  return floorGlow + uGrid * 0.85 * line * fade;
}

void main() {
  gl_FragColor = vec4(vUv.y > HORIZON ? sky(vUv) : ground(vUv), 1.0);
  #include <colorspace_fragment>
}
`;

/** The backdrop. */
export interface Backdrop {
  readonly mesh: Mesh;
  /** Tell the shader the width over the height of the canvas. A bad value is ignored. */
  setAspect(aspect: number): void;
  /** Show or hide the backdrop. */
  setVisible(on: boolean): void;
  dispose(): void;
}

/** Make the backdrop. */
export function createBackdrop(): Backdrop {
  const uAspect = { value: 1 };
  const material = new ShaderMaterial({
    uniforms: {
      uAspect,
      uGrid: { value: colorOf(TOKEN_HEX.force).clone() },
      uHot: { value: colorOf(TOKEN_HEX.brand).clone() },
      uSun: { value: colorOf(TOKEN_HEX.ask).clone() },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    depthTest: false,
    depthWrite: false,
  });
  material.name = "backdrop";
  const geometry = new PlaneGeometry(2, 2);
  const mesh = new Mesh(geometry, material);
  mesh.name = "backdrop";
  mesh.frustumCulled = false;
  mesh.renderOrder = -1000;
  return {
    mesh,
    setAspect(aspect) {
      if (Number.isFinite(aspect) && aspect > 0) uAspect.value = aspect;
    },
    setVisible(on) {
      mesh.visible = on;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}
